import { useState } from 'react';
import { Search, X, Grid, List, ArrowUpDown, SlidersHorizontal, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { DdqSession, SessionPriority } from '@/types/api.types';
import type { SessionSortOption } from '../utils/sortSessions';
import type { DateRange } from '../hooks/useFilteredSessions';

export type SessionStatus = DdqSession['status'];

export interface SessionFilterBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedStatuses: SessionStatus[];
  onStatusToggle: (status: SessionStatus) => void;
  selectedPriorities: SessionPriority[];
  onPriorityToggle: (priority: SessionPriority) => void;
  sortBy: SessionSortOption;
  onSortChange: (sort: SessionSortOption) => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  resultCount: number;
  className?: string;
}

const STATUSES: SessionStatus[] = ['Pending', 'Processing', 'Ready', 'Error'];
const PRIORITIES: { value: SessionPriority; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];
const SORT_OPTIONS: { value: SessionSortOption; label: string }[] = [
  { value: 'priority', label: 'Priority' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'automation', label: 'Automation Rate' },
  { value: 'name', label: 'Name' },
];

const STATUS_COLORS: Record<SessionStatus, string> = {
  Pending: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  Processing: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  Ready: 'bg-green-100 text-green-700 hover:bg-green-200',
  Error: 'bg-red-100 text-red-700 hover:bg-red-200',
};

const PRIORITY_COLORS: Record<SessionPriority, string> = {
  high: 'bg-red-100 text-red-700 hover:bg-red-200',
  normal: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  low: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
};

export function SessionFilterBar({
  searchQuery,
  onSearchChange,
  selectedStatuses,
  onStatusToggle,
  selectedPriorities,
  onPriorityToggle,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  dateRange,
  onDateRangeChange,
  resultCount,
  className,
}: SessionFilterBarProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilterCount =
    selectedStatuses.length + selectedPriorities.length + (dateRange.from ? 1 : 0) + (dateRange.to ? 1 : 0);

  const handleClearFilters = () => {
    selectedStatuses.forEach((status) => onStatusToggle(status));
    selectedPriorities.forEach((priority) => onPriorityToggle(priority));
    onDateRangeChange({ from: null, to: null });
  };

  const cycleSortOption = () => {
    const currentIndex = SORT_OPTIONS.findIndex((opt) => opt.value === sortBy);
    const nextIndex = (currentIndex + 1) % SORT_OPTIONS.length;
    onSortChange(SORT_OPTIONS[nextIndex].value);
  };

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {/* Search */}
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search questionnaires..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-9 bg-background border-border"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Sort Toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={cycleSortOption}
        className="gap-1.5"
      >
        <ArrowUpDown className="h-4 w-4" />
        <span className="hidden sm:inline">
          {SORT_OPTIONS.find((opt) => opt.value === sortBy)?.label}
        </span>
      </Button>

      {/* Filters Popover */}
      <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5">
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 justify-center text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="end">
          <div className="space-y-4">
            {/* Status */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Status</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {STATUSES.map((status) => {
                  const isSelected = selectedStatuses.includes(status);
                  return (
                    <Badge
                      key={status}
                      variant="outline"
                      className={cn(
                        'cursor-pointer transition-colors',
                        isSelected ? STATUS_COLORS[status] : 'hover:bg-muted'
                      )}
                      onClick={() => onStatusToggle(status)}
                    >
                      {status}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Priority</span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {PRIORITIES.map(({ value, label }) => {
                  const isSelected = selectedPriorities.includes(value);
                  return (
                    <Badge
                      key={value}
                      variant="outline"
                      className={cn(
                        'cursor-pointer transition-colors',
                        isSelected ? PRIORITY_COLORS[value] : 'hover:bg-muted'
                      )}
                      onClick={() => onPriorityToggle(value)}
                    >
                      {label}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <span className="text-sm font-medium">Date Range</span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input
                  type="date"
                  value={dateRange.from || ''}
                  onChange={(e) =>
                    onDateRangeChange({ ...dateRange, from: e.target.value || null })
                  }
                  className="h-8 text-xs"
                />
                <span className="text-muted-foreground text-xs">to</span>
                <Input
                  type="date"
                  value={dateRange.to || ''}
                  onChange={(e) =>
                    onDateRangeChange({ ...dateRange, to: e.target.value || null })
                  }
                  className="h-8 text-xs"
                />
              </div>
            </div>

            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="w-full text-xs"
              >
                Clear all filters
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* View Toggle */}
      <div className="flex items-center border rounded-md">
        <Button
          variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onViewModeChange('grid')}
          className="rounded-r-none"
        >
          <Grid className="h-4 w-4" />
        </Button>
        <Button
          variant={viewMode === 'list' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => onViewModeChange('list')}
          className="rounded-l-none"
        >
          <List className="h-4 w-4" />
        </Button>
      </div>

      {/* Result count */}
      <span className="text-sm text-muted-foreground ml-auto">
        {resultCount} session{resultCount !== 1 ? 's' : ''}
      </span>
    </div>
  );
}
